import { InvoiceType, ProceduresInvoice, TaxAuthorityActionResponse } from '../../models/invoice-v2.model';

export interface ProcedureActionFlags {
  isInProcedureCycle: boolean;
  showProcedureEntry: boolean;
  canCancel: boolean;
  canContinue: boolean;
  canFurtherObjection: boolean;
  canAfterHearingApproval: boolean;
  canReverseCharge: boolean;
  canResendSecond: boolean;
}

/** Minimal invoice shape used by procedure-cycle helpers (API rows are untyped). */
export interface ProcedureInvoiceLike {
  is_in_procedure_cycle?: unknown;
  confirm?: unknown;
  procedure?: unknown;
  allocation_number?: unknown;
  confirmation_number?: unknown;
  document_number?: unknown;
  invoice_type?: unknown;
  is_deleted?: unknown;
}

const ALLOCATION_DENIAL_HE = 'מספר הקצאה לחשבונית המס';

/** Document types eligible for "request allocation number" (tax authority). */
const ALLOCATION_REQUEST_INVOICE_TYPES = new Set<number>([
  InvoiceType.TAX_INVOICE,
  InvoiceType.INVOICE_WITH_RECEIPT,
  InvoiceType.REVERSE_INVOICE,
  InvoiceType.FINANCIAL_REQUEST,
]);

export function canRequestAllocationForType(invoiceType: number | null | undefined): boolean {
  return invoiceType != null && ALLOCATION_REQUEST_INVOICE_TYPES.has(+invoiceType);
}

function field(invoice: ProcedureInvoiceLike, key: keyof ProcedureInvoiceLike): unknown {
  return invoice[key];
}

function toNumber(value: unknown): number {
  if (value == null || value === '') return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

function toOptionalNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** API/DB may return boolean true, 1, or "1" — match legacy screens that used `== true`. */
export function isTruthyDbFlag(value: unknown): boolean {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0' || value == null || value === '') return false;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    return s === 'true' || s === 'yes';
  }
  return false;
}

export function isConfirmedFlag(value: unknown): boolean {
  return value === 1 || value === true || value === '1';
}

/** Backend sends to ITA when allocation_number === 1 and type supports send_to_tax_authority. */
export function isSentToTaxAuthority(invoice: ProcedureInvoiceLike | null | undefined): boolean {
  if (!invoice) return false;
  return toNumber(field(invoice, 'allocation_number')) === 1;
}

export function hasConfirmationNumber(invoice: ProcedureInvoiceLike | null | undefined): boolean {
  if (!invoice) return false;
  const conf = field(invoice, 'confirmation_number');
  return conf != null && String(conf).trim() !== '';
}

export function normalizeProcedureInvoiceRow<T extends ProcedureInvoiceLike>(row: T): T {
  if (!row) return row;
  return {
    ...row,
    is_in_procedure_cycle: isTruthyDbFlag(row.is_in_procedure_cycle),
    confirm: toOptionalNumber(row.confirm) ?? row.confirm,
    procedure:
      row.procedure == null || row.procedure === ''
        ? null
        : toOptionalNumber(row.procedure),
    allocation_number: toOptionalNumber(row.allocation_number) ?? row.allocation_number,
  };
}

export function getProcedureLabel(
  procedure: number | string | null | undefined,
  lang: string,
): string {
  const proc = procedure == null || procedure === '' ? null : toNumber(procedure);
  if (proc == null || Number.isNaN(proc)) {
    return '—';
  }
  switch (proc) {
    case ProceduresInvoice.DecisionContinue:
      return lang === 'ar'
        ? 'إصدار فاتورة بدون رقم تخصيص'
        : lang === 'he'
          ? 'הפקה ללא מספר הקצאה'
          : 'Issuing Without Allocation Number';
    case ProceduresInvoice.DecisionFurtherObjection:
      return lang === 'ar'
        ? 'طلب جلسة استماع'
        : lang === 'he'
          ? 'בקשת שימוע'
          : 'Request Hearing Session';
    case ProceduresInvoice.DecisionCancel:
      return lang === 'ar'
        ? 'إلغاء الفاتورة'
        : lang === 'he'
          ? 'ביטול חשבונית'
          : 'Canceling Invoice';
    case ProceduresInvoice.DecisionReversingTheCharge:
      return lang === 'ar'
        ? 'عكس الفاتورة على الزبون'
        : lang === 'he'
          ? 'היפוך חיוב'
          : 'Reversing the Charge';
    default:
      return String(proc);
  }
}

export function getLocalizedApiMessage(
  response: TaxAuthorityActionResponse | Record<string, unknown> | null | undefined,
  lang: string,
  fallback: string,
): string {
  if (!response) return fallback;
  const r = response as Record<string, unknown>;
  const msgAr = (r['messageAr'] ?? r['messageAR']) as string | undefined;
  const msgEn = (r['messageEn'] ?? r['messageEN']) as string | undefined;
  const msgHe = (r['messageHe'] ?? r['messageHE']) as string | undefined;
  const data = r['data'] as Record<string, unknown> | undefined;
  const dataMsg =
    data?.['message'] && typeof data['message'] === 'string'
      ? (data['message'] as string).trim()
      : null;

  let base = '';
  if (lang === 'ar' && msgAr) base = msgAr;
  else if (lang === 'he' && msgHe) base = msgHe;
  else if (msgEn) base = msgEn;
  else if (typeof r['message'] === 'string') base = r['message'] as string;
  else base = msgAr || msgHe || fallback;

  return dataMsg ? `${base} — ${dataMsg}` : base;
}

/**
 * Detect procedure-cycle entry after confirm/create+confirm.
 * Backend wraps 405 as success:true with Hebrew denial message (no top-level status:405).
 */
export function isProcedureCycleConfirmResponse(
  response: Record<string, unknown> | null | undefined,
): boolean {
  if (!response) return false;
  if (response['status'] === 405) return true;

  const data = response['data'] as ProcedureInvoiceLike | undefined;
  if (data && isTruthyDbFlag(data.is_in_procedure_cycle)) return true;
  if (isTruthyDbFlag(response['is_in_procedure_cycle'])) return true;

  // Confirmed without confirmation number + ITA denial text = entered procedure cycle (405 path).
  if (isConfirmedFlag(response['confirm']) && !hasConfirmationNumber(response)) {
    const msg = String(
      response['message'] ?? response['messageHe'] ?? response['messageAr'] ?? '',
    );
    if (msg.includes(ALLOCATION_DENIAL_HE) || msg.includes('מספר הקצאה')) {
      return true;
    }
  }
  return false;
}

/**
 * Action visibility aligned with InvoiceIntegrationV2Service rules.
 * Gear/modal entry: ONLY is_in_procedure_cycle (not merely missing confirmation_number).
 */
export function computeProcedureActionFlags(
  invoice: ProcedureInvoiceLike | null | undefined,
): ProcedureActionFlags {
  const empty: ProcedureActionFlags = {
    isInProcedureCycle: false,
    showProcedureEntry: false,
    canCancel: false,
    canContinue: false,
    canFurtherObjection: false,
    canAfterHearingApproval: false,
    canReverseCharge: false,
    canResendSecond: false,
  };
  if (!invoice) return empty;

  const proc = toOptionalNumber(field(invoice, 'procedure'));
  const inCycle = isTruthyDbFlag(field(invoice, 'is_in_procedure_cycle'));
  const invoiceType = toNumber(field(invoice, 'invoice_type'));
  const isReverseInvoice = invoiceType === 330;
  const isReceipt = invoiceType === 400 || invoiceType === 430;
  const isDeleted =
    field(invoice, 'is_deleted') === 1 || field(invoice, 'is_deleted') === true;
  const isConfirmed = isConfirmedFlag(field(invoice, 'confirm'));
  const sentToTax = isSentToTaxAuthority(invoice);
  const hasConf = hasConfirmationNumber(invoice);

  if (isDeleted) return empty;

  const isCancelled = proc === ProceduresInvoice.DecisionCancel;
  if (isCancelled) return empty;

  const isHearingRequested = proc === ProceduresInvoice.DecisionFurtherObjection;
  const isContinued = proc === ProceduresInvoice.DecisionContinue;
  const isReversed = proc === ProceduresInvoice.DecisionReversingTheCharge;

  // GET integration/cancel — in procedure cycle, confirmed, not receipt/reverse type.
  const canCancel =
    inCycle &&
    isConfirmed &&
    !isReverseInvoice &&
    !isReceipt;

  // GET integration/continue — blocked when procedure === 1 or 3.
  const canContinue = inCycle && !isContinued && !isCancelled;

  // GET integration/further-objection — hide if hearing already requested.
  const canFurtherObjection = inCycle && !isHearingRequested && !isCancelled;

  // GET integration/after-hearing-approval — requires procedure === 2.
  const canAfterHearingApproval = isHearingRequested && !isCancelled;

  // GET integration/reverse-charge — available in cycle until reversed or cancelled.
  const canReverseCharge =
    inCycle &&
    !isCancelled &&
    !isReversed &&
    !isReverseInvoice &&
    !isReceipt;

  const doc = field(invoice, 'document_number');
  const hasDocNumber = doc != null && String(doc).trim() !== '';

  // GET integration/send-second — used outside procedure panel (list/view dropdown).
  const canResendSecond =
    isConfirmed &&
    sentToTax &&
    hasDocNumber &&
    !hasConf &&
    proc !== ProceduresInvoice.DecisionFurtherObjection &&
    proc !== ProceduresInvoice.DecisionCancel;

  return {
    isInProcedureCycle: inCycle,
    showProcedureEntry: inCycle,
    canCancel,
    canContinue,
    canFurtherObjection,
    canAfterHearingApproval,
    canReverseCharge,
    canResendSecond,
  };
}
